import type {
  BulkAssignCandidatesMultiResult,
  BulkAssignCandidatesResult,
} from '@/types/recruitment';

export interface BulkAssignNotice {
  tone: 'success' | 'info' | 'warning';
  text: string;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

// HW-1055: candidates who revoked consent are left out by the backend; say so
// instead of reporting the add as done. A batch of only revoked candidates is
// refused with 409 CONSENT_REVOKED and shown as an error by the dialogs.
const revokedTail = (revoked: number) =>
  revoked > 0
    ? `${revoked} ${plural(revoked, 'candidate', 'candidates')} skipped: consent revoked`
    : '';

/**
 * Toast after "Assign to vacancy" for one candidate (candidate directory /
 * profile). A candidate who revoked consent never gets here — the backend
 * answers 409 for them, shown by the dialog as an error.
 */
export function singleAssignNotice(
  name: string,
  result: BulkAssignCandidatesMultiResult
): BulkAssignNotice {
  if (result.skipped === 0) {
    return {
      tone: 'success',
      text: `Assigned ${name} to ${result.added} ${plural(result.added, 'vacancy', 'vacancies')}`,
    };
  }
  if (result.added === 0) {
    return { tone: 'info', text: `${name} is already assigned to all selected vacancies` };
  }
  return {
    tone: 'success',
    text: `Assigned ${name} to ${result.added} vacancies, ${result.skipped} already in pipeline`,
  };
}

/** Toast after "Assign to vacancy" for a selection of candidates. */
export function multiAssignNotice(result: BulkAssignCandidatesMultiResult): BulkAssignNotice {
  const cCount = result.candidatesProcessed;
  const vCount = result.vacanciesProcessed;
  const text =
    result.skipped === 0
      ? `Assigned ${cCount} ${plural(cCount, 'candidate', 'candidates')} to ${vCount} ${plural(vCount, 'vacancy', 'vacancies')} (${result.added} new entries)`
      : `Created ${result.added} new pipeline ${plural(result.added, 'entry', 'entries')}, ${result.skipped} already in pipeline`;
  return result.revoked > 0
    ? { tone: 'warning', text: `${text}; ${revokedTail(result.revoked)}` }
    : { tone: 'success', text };
}

/** Toast after "Add existing candidates" on a vacancy. */
export function vacancyAddNotice(result: BulkAssignCandidatesResult): BulkAssignNotice {
  const text =
    result.skipped > 0
      ? `Added ${result.added}, skipped ${result.skipped} already in pipeline`
      : `Added ${result.added} ${plural(result.added, 'candidate', 'candidates')}`;
  return result.revoked > 0
    ? { tone: 'warning', text: `${text}; ${revokedTail(result.revoked)}` }
    : { tone: 'success', text };
}
